import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.pendingExits = 0
    this.heightSettledHandler = null

    this.content = this.element.closest("#content")
  }

  disconnect() {
    this.releaseContent()
  }

  // Identity-preserving merge of the incoming notices. Each notice is matched
  // by its id. Surviving notices are updated. Entrants are inserted at the top,
  // in incoming order. Exiting notices leave.
  //
  // Action notices are preserved when the incoming set contains none;
  // replaceActions overrides this, so an empty set clears them.
  // - Aaron, Thu Jun 11 2026
  merge(notices, { replaceActions = false } = {}) {
    // Pin the height before inserting entrants so the container doesn't jump to fit
    // them. Released below if nothing enters or leaves. - Aaron, Fri Jun 5 2026
    const initialHeight = this.pinHeight()

    // Each match is removed from newNotices as it is found, so what remains is exactly
    // the entrants.
    const newNotices = [...notices]
    const existingNotices = [...this.element.querySelectorAll(".notice")]
    const exitingNotices = []

    replaceActions ||= newNotices.some((notice) => getGroup(notice) === ACTION_GROUP)

    for (const existingNotice of existingNotices) {
      const index = newNotices.findIndex(newNotice => newNotice.id === existingNotice.id)

      if (index !== -1) {
        const [updatedNotice] = newNotices.splice(index, 1)

        this.getNoticeController(existingNotice).update(updatedNotice)
      } else {
        const group = getGroup(existingNotice)

        if (replaceActions || group !== ACTION_GROUP) {
          exitingNotices.push(existingNotice)
        }
      }
    }

    const anchor = this.element.querySelector(".notice")
    for (const notice of newNotices) {
      notice.classList.add("entering")

      // If the anchor is null, this will append - Aaron, Fri Jun 5 2026
      this.element.insertBefore(notice, anchor)
    }

    if (newNotices.length === 0 && exitingNotices.length === 0) {
      this.element.style.height = ""
      return
    }

    this.beginAnimation(initialHeight, exitingNotices)
  }

  // Remove the notice the user dismissed, exiting it with the same animation a
  // merge uses to retire a notice. - Aaron, Tue Jun 9 2026
  remove(event) {
    const notice = event.target.closest(".notice")

    const initialHeight = this.pinHeight()
    this.beginAnimation(initialHeight, [notice])
  }

  // Settle any in-progress animation and pin the container to its current height so
  // it does not jump as notices enter or leave. Returns the pinned height, which the
  // movement below is measured against. - Aaron, Tue Jun 9 2026
  pinHeight() {
    this.endAnimation()

    const initialHeight = this.getHeight()
    this.element.style.height = initialHeight + "px"

    return initialHeight
  }

  beginAnimation(initialHeight, exitingNotices) {
    // The height once exiting notices are gone - Aaron, Fri Jun 5 2026
    const finalHeight = this.getFinalHeight(exitingNotices)

    this.holdContent(initialHeight, finalHeight)

    if (exitingNotices.length > 0) {
      this.pendingExits = exitingNotices.length

      for (const notice of exitingNotices) {
        this.onNoticeExited(notice, () => this.noticeExited(exitingNotices, finalHeight))
        this.getNoticeController(notice).exit()
      }

      return
    }

    this.setHeight(finalHeight)
    this.onHeightSettled(() => this.endAnimation())
  }

  // Once the last exit has finished, collapse the exits and shrink the container to
  // the notices that remain. The last collapse ends the animation.
  noticeExited(exitingNotices, finalHeight) {
    this.pendingExits -= 1
    if (this.pendingExits > 0) {
      return
    }

    let pendingCollapses = exitingNotices.length
    for (const notice of exitingNotices) {
      this.onNoticeCollapsed(notice, () => {
        pendingCollapses -= 1
        if (pendingCollapses === 0) {
          this.endAnimation()
        }
      })

      this.getNoticeController(notice).collapse()
    }

    this.setHeight(finalHeight)
  }

  // Set the container height and hold the content against it in the same step: the
  // notices' margin-bottom takes back the held offset measured in holdContent,
  // leaving the content where it was. Both the height and the margin carry the same
  // transition, so they move together without the content being measured per frame.
  // - Aaron, Fri Jun 5 2026
  setHeight(height) {
    this.element.style.height = height + "px"

    if (this.holdingContent) {
      this.element.style.setProperty("--scroll-compensation", -this.heldOffset + "px")
    }
  }

  // Total computed (layout) height of the notices, excluding the given ones. Uses
  // the computed height, not getBoundingClientRect: an entrant is scaled, so its
  // rectangle is smaller than the height it settles to. - Aaron, Fri Jun 5 2026
  getFinalHeight(excluded = []) {
    return [...this.element.querySelectorAll(".notice")].
      filter((notice) => !excluded.includes(notice)).
      reduce((sum, notice) => sum + parseFloat(getComputedStyle(notice).height), 0)
  }

  // Measure how far the content below can be held while the height animates from
  // initialHeight to finalHeight, and turn the hold on. The held offset is the part of
  // the change the scroll can take up: a growth into the scroll room it opens below
  // (which a growth that does not make the content overflow never creates), a shrink
  // into the scroll there is above to give back. The rest of the change the content
  // moves outright — it moves only because the scroll cannot take it up, and it moves
  // over the transition rather than jumping at the end. The offset is measured here,
  // while the layout is still at initialHeight, and applied in setHeight alongside the
  // height so the two transition together. - Aaron, Fri Jun 5 2026
  holdContent(initialHeight, finalHeight) {
    if (this.content == null) {
      return
    }

    const delta = finalHeight - initialHeight
    const scrollTop = this.content.scrollTop
    const absorbable = delta > 0
      ? this.scrollRangeForHeight(finalHeight) - scrollTop
      : scrollTop

    this.heldOffset = Math.sign(delta) * Math.max(0, Math.min(Math.abs(delta), absorbable))

    if (this.heldOffset !== 0) {
      this.element.classList.add("scroll-compensating")
      this.holdingContent = true
    }
  }

  // The largest the content can be scrolled once the notices settle at the given
  // height, measured by pinning that height and reading the scroll range. The height
  // transition is suspended so the read sees the target rather than the value
  // mid-animation, and the pin is then restored — with the suspension flushed — so the
  // change still animates from where it is now. - Aaron, Fri Jun 5 2026
  scrollRangeForHeight(height) {
    const pinnedHeight = this.element.style.height

    this.element.style.transition = "none"
    this.element.style.height = height + "px"
    const range = this.content.scrollHeight - this.content.clientHeight
    this.element.style.height = pinnedHeight
    void this.element.offsetHeight
    this.element.style.transition = ""

    return range
  }

  // Hand the held offset back to the scroll position in one step, so the swap is
  // seamless: the held amount moves from the margin to the scroll. scrollTop is read
  // before the margin is dropped, then set as an absolute value after, because
  // dropping the margin resizes the scroll range, which would clamp a relative shift
  // against the wrong range. - Aaron, Fri Jun 5 2026
  releaseContent() {
    if (!this.holdingContent) {
      return
    }

    this.holdingContent = false

    const scrollTop = this.content.scrollTop + this.heldOffset
    this.element.classList.remove("scroll-compensating")
    this.element.style.removeProperty("--scroll-compensation")
    this.content.scrollTop = scrollTop
  }

  // Settle to a resting state: hand the content back to the scroll, unpin the height,
  // and clear the entering and exiting markers. Called both when a change's animation
  // completes and to settle an in-progress change before a new merge, so it must be
  // safe to call at any point, including when nothing is animating.
  // - Aaron, Sat Jun 6 2026
  endAnimation() {
    this.releaseContent()
    this.element.style.height = ""
    this.endEnteringAnimation()
    this.endExitingAnimation()

    // Drop the pending height-settled wait, if any, so an interrupted pure add does
    // not end the animation again when a later height transition ends.
    // - Aaron, Sat Jun 6 2026
    this.element.removeEventListener("transitionend", this.heightSettledHandler)
    this.heightSettledHandler = null
  }

  endEnteringAnimation() {
    for (const notice of this.element.querySelectorAll(".notice.entering")) {
      notice.classList.remove("entering")
    }
  }

  endExitingAnimation() {
    for (const notice of this.element.querySelectorAll(".notice.exiting")) {
      notice.remove()
    }
    this.pendingExits = 0
  }

  getHeight() {
    return this.element.getBoundingClientRect().height
  }

  getNoticeController(notice) {
    return this.application.getControllerForElementAndIdentifier(notice, "notice")
  }

  onNoticeExited(notice, callback) {
    notice.addEventListener("notice:exited", callback, { once: true })
  }

  onNoticeCollapsed(notice, callback) {
    notice.addEventListener("notice:collapsed", callback, { once: true })
  }

  // Wait for the container's height transition to end. The handler is kept so
  // endAnimation can remove it, whether it fired (the change finished) or not (a new
  // merge settled the change first). - Aaron, Sat Jun 6 2026
  onHeightSettled(callback) {
    this.heightSettledHandler = (event) => {
      if (event.target !== this.element || event.propertyName !== "height") {
        return
      }

      callback()
    }

    this.element.addEventListener("transitionend", this.heightSettledHandler)
  }
}

// A notice's group, in render order, carried as a class on the element.
const GROUP_CLASSES = ["page", "action", "form"]

const ACTION_GROUP = "action"

function getGroup(notice) {
  for (const groupClass of GROUP_CLASSES) {
    if (notice.classList.contains(groupClass)) {
      return groupClass
    }
  }

  return null
}
